import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodObject, type ZodType } from 'zod';
import type { FieldErrors, FieldValues, Resolver, ResolverResult } from 'react-hook-form';

/**
 * The object schema without its `.refine`/`.superRefine` checks. zod 4 keeps those checks on the
 * object itself and refuses `.pick()` on it, so the fields are rebuilt into a plain object.
 */
function objectSchemaOf(schema: ZodType): ZodObject {
  if (!(schema instanceof ZodObject)) {
    throw new Error('sectionResolver needs a zod object schema');
  }
  return z.object(schema.shape);
}

function pickErrors<T extends FieldValues>(
  errors: FieldErrors<T>,
  fields: readonly string[],
): FieldErrors<T> {
  const picked: Record<string, unknown> = {};
  for (const field of fields) {
    const error = (errors as Record<string, unknown>)[field];
    if (error) picked[field] = error;
  }
  return picked as FieldErrors<T>;
}

/**
 * A react-hook-form resolver that validates the whole profile schema but only
 * reports errors for one section's fields (FR-167, profile-redesign 02-design §2).
 *
 * The side-panel form holds the full profile, seeded from saved data, so the
 * schema's cross-field rules (e.g. min <= max round size) still run. What it
 * must not do is block a save on a field the member cannot see in this panel
 * - a stale saved value elsewhere would otherwise make the section unsaveable
 * with the error hidden. In that case this section is parsed on its own and
 * merged over the raw values, so the payload still gets this section's
 * coerced values.
 *
 * @param schema - the module's full profile schema (an object, optionally refined).
 * @param fields - the top-level field names the panel edits.
 * @returns a resolver for `useForm`.
 * @throws Error when `schema` is not a (refined) zod object.
 */
export function sectionResolver<T extends FieldValues>(
  schema: ZodType,
  fields: readonly string[],
): Resolver<T> {
  const full = zodResolver(schema as ZodType<T, T>) as Resolver<T>;
  const mask = Object.fromEntries(fields.map((field) => [field, true])) as Record<string, true>;
  const sectionSchema = objectSchemaOf(schema).pick(mask);

  return async (values, context, options) => {
    const result = await full(values, context, options);
    const sectionErrors = pickErrors(result.errors, fields);
    if (Object.keys(sectionErrors).length > 0) {
      return { values: {}, errors: sectionErrors } as ResolverResult<T>;
    }
    if (Object.keys(result.errors).length === 0) {
      return result;
    }
    const parsed = sectionSchema.safeParse(values);
    const sectionValues = parsed.success ? parsed.data : {};
    return { values: { ...values, ...sectionValues }, errors: {} } as ResolverResult<T>;
  };
}
